import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageShell, btnDanger, btnPrimary, cellCls, headCls, inputCls } from "@/components/TopBar";

const EMPTY = { username: "", password: "", full_name: "", role: "dispatcher", email: "", phone: "" };

const ROLE_NOTE = {
  admin: "Full access - users, vehicles, drivers, geofences",
  dispatcher: "Fleet + trips + geofences, no user or vehicle admin",
  driver: "Pushes location from the driver app",
  viewer: "View-only client access to the live fleet and trips",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  useEffect(() => {
    load().catch(() => toast.error("Could not load users"));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.username.toUpperCase()} created`);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.username}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <PageShell
      testid="users-page"
      subtitle="super admin"
      title="USER ACCESS"
      actions={
        <button data-testid="new-user-btn" onClick={() => setShowForm((s) => !s)} className={btnPrimary}>
          <Plus size={12} /> {showForm ? "CLOSE" : "ADD USER"}
        </button>
      }
    >
      {showForm && (
        <form
          onSubmit={create}
          data-testid="user-form"
          className="mb-8 grid gap-4 border border-white/15 bg-[#121212] p-5 sm:grid-cols-3"
        >
          <div>
            <label className="overline mb-2 block">username</label>
            <input
              data-testid="user-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="CLIENT1"
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">password</label>
            <input
              data-testid="user-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">role</label>
            <select
              data-testid="user-role-select"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className={inputCls}
            >
              <option value="admin">admin</option>
              <option value="dispatcher">dispatcher</option>
              <option value="driver">driver</option>
              <option value="viewer">viewer (view only)</option>
            </select>
          </div>
          <div>
            <label className="overline mb-2 block">full name</label>
            <input
              data-testid="user-fullname-input"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className="overline mb-2 block">email</label>
            <input
              data-testid="user-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy} data-testid="submit-user-btn" className={`${btnPrimary} w-full py-2.5`}>
              {busy ? "creating" : "create user"}
            </button>
          </div>
          <p className="overline sm:col-span-3">{ROLE_NOTE[form.role]}</p>
        </form>
      )}

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[720px]" data-testid="users-table">
          <thead className="border-b border-white/10 bg-[#121212]">
            <tr>
              <th className={headCls}>username</th>
              <th className={headCls}>name</th>
              <th className={headCls}>role</th>
              <th className={headCls}>email</th>
              <th className={headCls}>actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.username}`} className="border-b border-white/5 hover:bg-[#141414]">
                <td className={`${cellCls} font-head font-bold`}>{u.username}</td>
                <td className={cellCls}>{u.full_name || "-"}</td>
                <td className={cellCls} style={{ color: u.role === "viewer" ? "#FFCC00" : "#00F0FF" }}>
                  {u.role === "viewer" ? "VIEW ONLY" : u.role?.toUpperCase()}
                </td>
                <td className={`${cellCls} text-[#A1A1AA]`}>{u.email || "-"}</td>
                <td className={cellCls}>
                  {u.id === user?.id ? (
                    <span className="overline">current session</span>
                  ) : (
                    <button
                      data-testid={`delete-user-btn-${u.username}`}
                      onClick={() => remove(u)}
                      className={btnDanger}
                      aria-label="Delete user"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
